import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';

interface FeeLedgerRow {
  id: string;
  hubId: string;
  assignmentId: string;
  orderId: string;
  orderSourceType: string;
  deliveryPartnerId?: string;
  feeAmount?: number;
  status: string;
  weekBucket?: string;
  deliveredAt?: string;
  createdOn?: string;
  notes?: string;
}

/**
 * TinyAll Delivery Network — admin view of the network-fee ledger with status filter.
 * Supports manual status changes (waive / review / charge). No Stripe charge is triggered (MVP-1).
 * All endpoints 404 when the backend feature flag (TINYALL_DELIVERY_NETWORK_ENABLED) is off.
 */
@Component({
  selector: 'app-delivery-fee-ledger',
  templateUrl: './delivery-fee-ledger.component.html',
  styleUrls: ['./delivery-fee-ledger.component.scss'],
})
export class DeliveryFeeLedgerComponent implements OnInit {
  private readonly apiUrl = environment.apiUrl;
  private readonly baseUrl = `${this.apiUrl}/admin-center/delivery-network/v1`;

  data: FeeLedgerRow[] = [];
  isLoading = false;
  featureDisabled = false;

  readonly statusOptions = ['', 'PENDING', 'WAIVED', 'CHARGED', 'REVIEW'];
  statusFilter = '';
  hubFilter = '';

  constructor(
    private httpService: HttpService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    const params: string[] = [];
    if (this.statusFilter) {
      params.push(`status=${encodeURIComponent(this.statusFilter)}`);
    }
    if (this.hubFilter?.trim()) {
      params.push(`hubId=${encodeURIComponent(this.hubFilter.trim())}`);
    }
    const query = params.length ? `?${params.join('&')}` : '';
    try {
      const response = await this.httpService
        .doGet(`${this.baseUrl}/fee-ledger${query}`)
        .toPromise();
      this.data = Array.isArray(response) ? response : [];
      this.featureDisabled = false;
    } catch (error: any) {
      this.data = [];
      if (error?.status === 404) {
        this.featureDisabled = true;
      } else {
        this.toastError('Could not load fee ledger');
      }
    } finally {
      this.isLoading = false;
    }
  }

  confirmSetStatus(row: FeeLedgerRow, status: string): void {
    this.confirmationService.confirm({
      message: `Set ledger row for order ${row.orderId} to ${status}? (No Stripe charge is performed.)`,
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.setStatus(row, status),
    });
  }

  private async setStatus(row: FeeLedgerRow, status: string): Promise<void> {
    try {
      await this.httpService
        .doPatch(`${this.baseUrl}/fee-ledger/${encodeURIComponent(row.id)}/status`, { status })
        .toPromise();
      this.toastSuccess(`Ledger row set to ${status}`);
      this.loadData();
    } catch (error: any) {
      this.toastError(error?.error?.message || 'Could not update ledger status');
    }
  }

  statusSeverity(status: string): string {
    switch (status) {
      case 'CHARGED':
        return 'status-CHARGED';
      case 'WAIVED':
        return 'status-WAIVED';
      case 'REVIEW':
        return 'status-REVIEW';
      default:
        return 'status-PENDING';
    }
  }

  private toastSuccess(detail: string): void {
    this.messageService.add({ severity: 'success', summary: 'Successful', detail, life: 3000 });
  }

  private toastError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail, life: 4000 });
  }
}
